import { memo, useCallback, useState } from 'react';
import { useCustomerModuleModalRootActionsStore } from '@/features/customers/_shared/components/customer-modal-root/customer-modal-root.store';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '@/components/_ui/modal';
import { Star } from 'lucide-react';

/**
 * StarRating - Interactive star rating component with hover effects
 */
const StarRating = memo<{ questionId: number }>(({ questionId: _questionId }) => {
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);

  return (
    <div className="flex justify-center gap-[8px]" onMouseLeave={() => setHoveredRating(0)}>
      {[1, 2, 3, 4, 5].map((rating) => {
        const isHighlighted = rating <= (hoveredRating || selectedRating);
        return (
          <button
            key={rating}
            type="button"
            className="p-[4px] hover:scale-110 transition-transform cursor-pointer"
            onMouseEnter={() => setHoveredRating(rating)}
            onClick={() => setSelectedRating(rating)}
          >
            <Star
              className={`w-[38px] h-[38px] transition-colors ${
                isHighlighted ? 'text-[#FFB800] fill-[#FFB800]' : 'text-[#D9D9D9]'
              }`}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
});

StarRating.displayName = 'StarRating';

const mockCSATPreview: ICSATPreviewModalProps['questions'] = [
  {
    id: '1',
    title: 'Please rate your experience today.',
    detail: '',
    type: 'rating',
  },
  {
    id: '2',
    title: 'How would you rate our overall service performance?',
    detail: '',
    type: 'rating',
  },
  {
    id: '3',
    title: 'Do you have any suggestions or feedback to help us improve our service?',
    detail: '',
    type: 'text',
  },
  {
    id: '4',
    title: 'What do you like most about our service?',
    detail: '',
    type: 'text',
  },
];

export type TCSATPreviewModalType = 'CSATPreviewModal';
export interface ICSATPreviewModalProps {
  questionTitle?: string;
  questionDescription?: string;
  questions?: Array<{
    id: string;
    title: string;
    detail: string;
    type: 'text' | 'rating';
    createdAt?: string;
  }>;
  questionCount?: number;
}

const CSATPreviewModal = memo(({ questions }: ICSATPreviewModalProps) => {
  const customerModalRootActions = useCustomerModuleModalRootActionsStore();
  const [isSubmitPreview, setIsSubmitPreview] = useState(false);

  const isOpen = customerModalRootActions.checkIsOpenModal('CSATPreviewModal');

  const handleClose = useCallback(() => {
    customerModalRootActions.closeModal('CSATPreviewModal');
  }, [customerModalRootActions]);

  // Use provided questions or fallback to mock data; sort by detail so order matches CSAT page
  const displayQuestions = questions && questions.length > 0 ? questions : [];
  const sortedQuestions = [...displayQuestions].sort((a, b) =>
    (a.detail ?? '').localeCompare(b.detail ?? '', undefined, { sensitivity: 'base' })
  );
  const displayRatingQuestions =
    sortedQuestions.length > 0 ? sortedQuestions.filter((question) => question.type === 'rating') : [];
  const displayTextQuestions =
    sortedQuestions.length > 0 ? sortedQuestions.filter((question) => question.type === 'text') : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCloseOutside={true}
      isButtonClose={true}
      className="md:w-[490px] w-[365px] p-[48px]"
      lockBodyScroll
    >
      <ModalHeader>
        <ModalTitle>CSAT Preview</ModalTitle>
      </ModalHeader>

      <ModalContent className="p-0">
        {/* CSAT Preview Card */}
        <div className="border border-[#F1F5F9] rounded-[8px] border-t-[10px]  border-t-[#0C8DBF] p-[24px] bg-[#F1F5F9]">
          {!isSubmitPreview ? (
            <>
              <div>
                <h3 className="text-[16px] font-[700] text-center">Thank you for contacting the NoLimit Indonesia.</h3>
              </div>

              {/* Questions List */}
              <div className="flex flex-col gap-[24px] mt-[16px]">
                {displayRatingQuestions.length === 0 && displayTextQuestions.length === 0 ? (
                  <div className="text-center text-[12px] text-[#9D9D9C] py-4">No questions configured</div>
                ) : (
                  <>
                    {displayRatingQuestions?.length > 0
                      ? displayRatingQuestions.map((question, index) => {
                          return (
                            <div key={index + 1} className="flex flex-col gap-[8px]">
                              {/* Question Title & Detail */}
                              <div className="text-start">
                                <h3 className="text-[12px] font-[400] text-[#25151E]">{question.detail}</h3>
                              </div>

                              {/* Rating Type */}
                              <StarRating questionId={index} />
                            </div>
                          );
                        })
                      : null}

                    {displayTextQuestions?.length > 0
                      ? displayTextQuestions.map((question, index) => {
                          return (
                            <div key={index + 1} className="flex flex-col gap-[8px]">
                              {/* Question Title & Detail */}
                              <div className="text-start">
                                <h3 className="text-[12px] font-[400] text-[#25151E]">{question.detail}</h3>
                              </div>

                              {/* Text Type */}
                              <textarea
                                placeholder="Type your answer here..."
                                className="w-full h-[80px] p-[12px] border border-[#D9D9D9] rounded-[8px] text-[12px] resize-none focus:outline-none focus:border-[#0C8DBF] bg-white"
                              />
                            </div>
                          );
                        })
                      : null}
                  </>
                )}
              </div>

              {/* Submit Button Preview */}
              {/* <div className="mt-[24px]">
                <button
                  onClick={() => setIsSubmitPreview(true)}
                  type="button"
                  className="w-full py-[10px] bg-[#0C8DBF] text-white text-[14px] font-[400] rounded-[8px] cursor-pointer hover:bg-[#0C8DBF]/80 transition-colors"
                >
                  Submit
                </button>
              </div> */}
            </>
          ) : (
            <>
              <div>
                <h3 className="text-[16px] font-[700] text-center">Thank you for contacting the NoLimit Indonesia.</h3>
              </div>
              <div className="mt-[16px]">
                <button
                  onClick={handleClose}
                  type="button"
                  className="w-full py-[10px] bg-[#0C8DBF] text-white text-[14px] font-[400] rounded-[8px] cursor-pointer hover:bg-[#0C8DBF]/80 transition-colors"
                >
                  Submit
                </button>
              </div>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
});

CSATPreviewModal.displayName = 'CSATPreviewModal';
export default CSATPreviewModal;
